// Opt-out de resumo automático — Ajustes > Privacidade (LOTE PRIVACIDADE 1,
// 06/09/2026).
//
// Por que existe: em 21/08 um usuário exerceu o direito de limitar o uso dos
// seus dados sensíveis e o sistema não tinha alavanca nenhuma — o
// mirror-summarize seguiu resumindo (religião, sexualidade, estado
// emocional). A coluna user_profiles.summaries_opt_out é a alavanca; esta
// linha é o autoatendimento dela. O gerador lê a marca ANTES de chamar o
// modelo e falha fechado; a UI só precisa gravar o valor certo e mostrar o
// valor que o banco TEM.
//
// Contrato de persistência = o dos outros toggles de Settings (prod 11/06):
// otimista → persiste e AGUARDA → qualquer falha reverte + toast + Sentry.
// Mais o §4.9 do CLAUDE.md: .select() depois do .update(), e 0 linhas é
// falha — um perfil sem linha faria o UPDATE "dar certo" sem gravar nada,
// exatamente o "pedido registrado, sistema não obedeceu" de 21/08.
//
// Skeleton enquanto o valor real não chegou (value === null): inclusive
// quando a coluna ainda não existe no banco (migration não aplicada). Um OFF
// inventado seria mentira, e o toggle nunca escreve sobre estado não
// hidratado.
//
// Texto honesto: o tradeoff diz que ligar PIORA a memória do Espelho.
// Privacidade não se vende como só ganho. src/test/summariesOptOutRow.test.tsx
// pina que as seis línguas falam de memória.
//
// Componente próprio (e não JSX inline na SettingsPage) porque a página não
// tem harness de teste e o LOTE exige provar que o controle escreve a coluna
// e lê o valor certo.

import { useEffect, useState } from "react";
import { EyeOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Sentry } from "@/lib/sentry";
import { Toggle } from "@/pages/SettingsPage";

/** Coluna de user_profiles que esta linha lê e escreve. */
export const SUMMARIES_OPT_OUT_COLUMN = "summaries_opt_out";

/**
 * Valor da marca a partir da linha de perfil que a SettingsPage já carregou
 * (select("*")). null = não disponível: perfil ainda não carregou, falhou, ou
 * a coluna não existe na linha (migration não aplicada). Só um boolean REAL
 * vira toggle — nunca um default inventado.
 */
export function summariesOptOutFromProfile(profile: unknown): boolean | null {
  if (!profile || typeof profile !== "object") return null;
  const v = (profile as Record<string, unknown>)[SUMMARIES_OPT_OUT_COLUMN];
  return typeof v === "boolean" ? v : null;
}

interface UpdateResult {
  data: Array<Record<string, unknown>> | null;
  error: { message?: string } | null;
}

interface Props {
  userId: string | null;
  value: boolean | null;
}

export default function SummariesOptOutRow({ userId, value }: Props) {
  const { t } = useLanguage();
  const [on, setOn] = useState<boolean | null>(value);
  const [busy, setBusy] = useState(false);

  // O perfil chega depois da montagem: ressincroniza quando o valor real vier.
  useEffect(() => {
    setOn(value);
  }, [value]);

  const persist = async (next: boolean) => {
    if (on === null || !userId || busy) return;
    const prev = on;
    setOn(next);
    setBusy(true);
    try {
      // Coluna fora de types.ts (regenerar types é trabalho próprio, R3) —
      // o cast é o mesmo dos outros toggles dinâmicos da SettingsPage.
      const res = (await (supabase.from("user_profiles") as any)
        .update({ [SUMMARIES_OPT_OUT_COLUMN]: next })
        .eq("user_id", userId)
        .select(SUMMARIES_OPT_OUT_COLUMN)) as UpdateResult;
      if (res.error) throw res.error;
      if (!res.data || res.data.length === 0) {
        throw new Error("user_profiles update matched 0 rows (no profile row?)");
      }
      // O que fica na tela é o que o banco devolveu, não o que pedimos.
      setOn(res.data[0][SUMMARIES_OPT_OUT_COLUMN] === true);
    } catch (err) {
      // §11: captureException no mesmo handler → a linha de console é warn
      // (vira breadcrumb do evento), não um segundo evento solto.
      console.warn("[SummariesOptOutRow] failed to save summaries opt-out:", err);
      setOn(prev);
      toast.error(t("settings.saveError") || "Error saving");
      try {
        Sentry.captureException(err, { tags: { feature: "summaries_opt_out_save_failed" } });
      } catch { /* Sentry not initialized in dev */ }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-4 flex items-start gap-3.5" data-testid="summaries-opt-out-row">
      <div className="w-8 h-8 rounded-lg bg-foreground/[0.06] flex items-center justify-center shrink-0 mt-0.5">
        <EyeOff size={14} className="text-foreground/50" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[13px] font-medium text-foreground/85">
          {t("settings.privacy.summariesOptOut.title")}
        </p>
        <p className="text-muted-foreground text-[13px] leading-relaxed mt-0.5 font-body">
          {t("settings.privacy.summariesOptOut.desc")}
        </p>
        <p className="text-muted-foreground text-[13px] leading-relaxed mt-1 font-body">
          {t("settings.privacy.summariesOptOut.tradeoff")}
        </p>
      </div>
      {/* Skeleton até o estado REAL do banco chegar — nunca um default. */}
      {on === null ? (
        <div
          className="w-11 h-6 rounded-full bg-foreground/[0.08] animate-pulse shrink-0 mt-0.5"
          data-testid="summaries-opt-out-skeleton"
        />
      ) : (
        <Toggle label={t("settings.privacy.summariesOptOut.title")} disabled={busy} on={on} onToggle={() => persist(!on)} />
      )}
    </div>
  );
}
